import { ErrorBanner } from '../ui/index.jsx'

interface TemplateDept {
  name: string
  color: string
}

interface OnboardingTemplate {
  id: string
  name: string
  description: string
  departments: TemplateDept[]
}

interface TemplateResult {
  departments: number
  categories: number
  locations: number
}

interface StepConfigEntry {
  title?: string
  subtitle?: string
  [key: string]: unknown
}

interface TemplateStepProps {
  stepConfig: Record<string, StepConfigEntry>
  templates: OnboardingTemplate[]
  selectedTemplate: string | null
  setSelectedTemplate: (id: string | null) => void
  templateApplied: boolean
  templateResult: TemplateResult | null
  applyTemplate: () => void
  error: string
  setError: (err: string) => void
  loading: boolean
  goNext: () => void
  t: (key: string) => string
}

const TEMPLATE_ICONS: Record<string, string> = {
  'theater-200': '🎭',
  'touring': '🚛',
  'rental-house': '🏢',
  'venue-booking': '🎵',
  'gallery': '🖼️',
}

export default function TemplateStep({
  stepConfig, templates, selectedTemplate, setSelectedTemplate,
  templateApplied, templateResult, applyTemplate,
  error, setError, loading, goNext, t,
}: TemplateStepProps) {
  return (
    <div>
      <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
        {stepConfig.template?.title || t('quickSetup')}
      </h2>
      <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)', marginBottom: 24 }}>
        {stepConfig.template?.subtitle || t('quickSetupDesc')}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {templates.map(tmpl => {
          const selected = selectedTemplate === tmpl.id
          return (
            <button
              key={tmpl.id}
              onClick={() => setSelectedTemplate(selected ? null : tmpl.id)}
              style={{
                display: 'flex', gap: 14, alignItems: 'flex-start',
                background: selected ? 'var(--accent-bg, rgba(99,102,241,0.1))' : 'var(--bg-elevated)',
                border: selected ? '2px solid var(--accent)' : '2px solid var(--border-default)',
                borderRadius: 12, padding: '14px 16px',
                cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ fontSize: 'var(--text-3xl)', marginTop: 2, flexShrink: 0 }}>
                {TEMPLATE_ICONS[tmpl.id] || '📋'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: 'var(--text-base)', fontWeight: 600, margin: '0 0 4px',
                  color: 'var(--text-primary)',
                }}>
                  {tmpl.name}
                </p>
                <p style={{
                  fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: '0 0 8px',
                  lineHeight: 1.4,
                }}>
                  {tmpl.description}
                </p>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {tmpl.departments.map(d => (
                    <span key={d.name} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 'var(--text-xs)', color: 'var(--text-secondary)',
                      background: 'var(--bg-surface)', borderRadius: 4,
                      padding: '2px 6px',
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: d.color, flexShrink: 0,
                      }} />
                      {d.name}
                    </span>
                  ))}
                </div>
              </div>
              {selected && (
                <span style={{
                  fontSize: 'var(--text-xl)', color: 'var(--accent)', flexShrink: 0,
                  marginTop: 2,
                }}>
                  ✓
                </span>
              )}
            </button>
          )
        })}
      </div>

      {templateApplied && templateResult && (
        <p style={{
          fontSize: 'var(--text-caption)', color: 'var(--success, #22c55e)', marginBottom: 12,
          background: 'var(--bg-elevated)', borderRadius: 8, padding: '8px 12px',
        }}>
          {t('templateApplied')} — {templateResult.departments} {t('departments')}, {templateResult.categories} {t('categories')}, {templateResult.locations} {t('locations')}.
        </p>
      )}

      <ErrorBanner style={{ fontSize: 'var(--text-caption)', marginBottom: 12 }}>{error}</ErrorBanner>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => { setError(''); goNext() }}
          style={{
            flex: 1, padding: '13px', borderRadius: 10,
            background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
            border: '1px solid var(--border-default)', fontSize: 'var(--text-base)',
            cursor: 'pointer',
          }}
        >
          {t('skipForNow')}
        </button>
        <button
          onClick={applyTemplate}
          disabled={loading || templateApplied}
          style={{
            flex: 2, padding: '13px', borderRadius: 10,
            background: (loading || templateApplied) ? 'var(--bg-elevated)' : 'var(--accent)',
            color: (loading || templateApplied) ? 'var(--text-muted)' : 'var(--text-inverse)',
            border: 'none', fontSize: 'var(--text-md)', fontWeight: 600,
            cursor: (loading || templateApplied) ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? t('applyingTemplate') : templateApplied ? t('applied') : selectedTemplate ? t('applyTemplate') : t('continueArrow')}
        </button>
      </div>
    </div>
  )
}
